import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom'
import { ScrollToTop } from './components/ScrollToTop'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'sonner'
import { AuthProvider } from './hooks/authContext'

// Pages
import Home from './pages/Home'
import Login from './pages/Login'
import Register from './pages/Register'
import RegisterSuccess from './pages/RegisterSuccess'
import Dashboard from './pages/Dashboard'
import Brand from './pages/Brand'
import Products from './pages/Products'
import Subscription from './pages/Subscription'
import Settings from './pages/Settings'
import LandingPage from './pages/LandingPage'
import ProductPage from './pages/ProductPage'
import CatalogDirectory from './pages/CatalogDirectory'
import Articles from './pages/Articles'
import ArticleDetail from './pages/ArticleDetail'
import Testimonials from './pages/Testimonials'
import NotFound from './pages/NotFound'
import Privacy from './pages/Privacy'
import PromoPage from './pages/PromoPage'
import BrandArticles from './pages/BrandArticles'
import MarketingDashboard from './pages/marketing/MarketingDashboard'
import MarketingCampaign from './pages/marketing/MarketingCampaign'
import MarketingBanner from './pages/marketing/MarketingBanner'
import MarketingCopywriter from './pages/marketing/MarketingCopywriter'
import MarketingCalendar from './pages/marketing/MarketingCalendar'
import MarketingPlanner from './pages/marketing/MarketingPlanner'
import MarketingHistory from './pages/marketing/MarketingHistory'
import MarketingAnalytics from './pages/marketing/MarketingAnalytics'
import MarketingAiBoost from './pages/marketing/MarketingAiBoost'
import { MarketingLayout } from './components/marketing/MarketingLayout'
import { getSubdomainFromHost } from './lib/subdomain'

// Components
import { Navbar } from './components/Navbar'
import { ProtectedRoute } from './components/ProtectedRoute'

const queryClient = new QueryClient()

function AppLayout() {
  const location = useLocation()
  const hideNavbar =
    location.pathname.startsWith('/catalog/') && location.pathname !== '/catalog'

  return (
    <div className="min-h-screen bg-gray-50">
      {!hideNavbar && <Navbar />}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/artikel" element={<Articles />} />
        <Route path="/artikel/:slug" element={<ArticleDetail />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/register-success" element={
          <ProtectedRoute>
            <RegisterSuccess />
          </ProtectedRoute>
        } />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />
        <Route path="/brand" element={
          <ProtectedRoute>
            <Brand />
          </ProtectedRoute>
        } />
        <Route path="/products" element={
          <ProtectedRoute>
            <Products />
          </ProtectedRoute>
        } />
        <Route path="/subscription" element={
          <ProtectedRoute>
            <Subscription />
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        } />
        <Route path="/testimoni" element={
          <ProtectedRoute>
            <Testimonials />
          </ProtectedRoute>
        } />
        <Route path="/artikel-usaha" element={
          <ProtectedRoute>
            <BrandArticles />
          </ProtectedRoute>
        } />
        <Route path="/marketing" element={
          <ProtectedRoute>
            <MarketingLayout />
          </ProtectedRoute>
        }>
          <Route index element={<MarketingDashboard />} />
          <Route path="campaign" element={<MarketingCampaign />} />
          <Route path="banner" element={<MarketingBanner />} />
          <Route path="copywriter" element={<MarketingCopywriter />} />
          <Route path="calendar" element={<MarketingCalendar />} />
          <Route path="planner" element={<MarketingPlanner />} />
          <Route path="history" element={<MarketingHistory />} />
          <Route path="analytics" element={<MarketingAnalytics />} />
          <Route path="ai-boost" element={<MarketingAiBoost />} />
        </Route>
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/promo" element={<PromoPage />} />
        <Route path="/catalog" element={<CatalogDirectory />} />
        <Route path="/catalog/:brandSlug/:productSlug" element={<ProductPage />} />
        <Route path="/catalog/:subdomain" element={<LandingPage />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      <Toaster 
        position="top-right" 
        toastOptions={{
          style: {
            background: 'white',
            border: '1px solid #e5e7eb',
          },
        }}
      />
    </div>
  )
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <AppRoot />
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  )
}

/** Path yang memakai layout utama (dashboard, brand, login, dll.) — bukan katalog subdomain */
const APP_SHELL_PREFIXES = [
  '/dashboard',
  '/brand',
  '/products',
  '/subscription',
  '/settings',
  '/testimoni',
  '/artikel-usaha',
  '/marketing',
  '/login',
  '/register',
  '/register-success',
]

function isAppShellPath(pathname: string) {
  if (APP_SHELL_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`))) {
    return true
  }
  if (pathname === '/catalog' || pathname.startsWith('/catalog/')) return true
  if (pathname === '/artikel' || pathname.startsWith('/artikel/')) return true
  if (pathname === '/privacy' || pathname === '/promo') return true
  return false
}

function AppRoot() {
  const subdomain = typeof window !== 'undefined' ? getSubdomainFromHost() : null
  const { pathname } = useLocation()

  // Subdomain Premium: root = katalog; /brand, /dashboard, dll. tetap ke halaman admin
  if (subdomain && !isAppShellPath(pathname)) {
    return <LandingPage forcedSlug={subdomain} />
  }

  return <AppLayout />
}

export default App
